// changeDmlTarget — VQE-Edit edit-action: point an UPDATE / DELETE / MERGE /
// INSERT at a different table via SR-Edit's `change-dml-target-table` op. The planner
// replaces only the qualified name, so the statement's alias survives.
//
// The new name comes from the schema snapshot — the catalog's own spelling —
// so each part is delimited only when the engine could not read it back bare
// as that exact name (`needsDelimitersToNameExactly`: odd characters, a
// reserved word, or a case the engine would fold away), using the engine's
// own delimiter (`[x]`, `` `x` ``, `"x"`). Everything else is written bare.

import type { SqlEngine } from '../../../../sql-grammar/model';
import {
  ENGINE_DEFAULT_QUOTING,
  needsDelimitersToNameExactly,
  quoteWithStyle,
} from '../../../../sql-resolver/ddl/apply/dialect.quoting';
import type { Table } from '../../../../sql-resolver/types/schema';
import type { DmlTargetKind } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

/** One catalog name part, delimited only when the engine needs it to read that exact name back. */
export function formatIdentifierPart(name: string, engine: SqlEngine): string {
  return needsDelimitersToNameExactly(name, engine)
    ? quoteWithStyle(name, ENGINE_DEFAULT_QUOTING[engine])
    : name;
}

/** `schema.name` for a snapshot table, delimited per engine where required. */
export function formatDmlTargetName(table: Table, engine: SqlEngine): string {
  const name = formatIdentifierPart(table.qualified.name, engine);
  const schema = table.qualified.schema;
  return schema ? `${formatIdentifierPart(schema, engine)}.${name}` : name;
}

export function changeDmlTarget(
  target: DmlTargetKind,
  table: Table,
  ctx: CommitEditCtx,
): Promise<CommitOutcome> {
  const text = formatDmlTargetName(table, ctx.service.engineId);
  return commitEdit({ op: 'change-dml-target-table', target, text }, ctx);
}
